import React, { useState, useCallback, useRef, useEffect } from 'react';
import './SpreadsheetTable.css';

interface CellPos {
  row: number;
  col: number;
}

export interface SpreadsheetTableProps {
  headers: string[];
  rows: (string | number | null)[][];
  showRowNumbers?: boolean;
  maxHeight?: number;
  rowClasses?: (string | undefined)[];
  cellClasses?: (string | undefined)[][];
}

function getSelectionRect(anchor: CellPos, focus: CellPos) {
  return {
    minRow: Math.min(anchor.row, focus.row),
    maxRow: Math.max(anchor.row, focus.row),
    minCol: Math.min(anchor.col, focus.col),
    maxCol: Math.max(anchor.col, focus.col),
  };
}

function formatCell(value: string | number | null): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(4);
  }
  return String(value);
}

export const SpreadsheetTable: React.FC<SpreadsheetTableProps> = ({
  headers,
  rows,
  showRowNumbers = true,
  maxHeight = 500,
  rowClasses,
  cellClasses,
}) => {
  const [anchor, setAnchor] = useState<CellPos | null>(null);
  const [focus, setFocus] = useState<CellPos | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const lastCol = headers.length - 1;
  const lastRow = rows.length - 1;

  const sel = anchor && focus ? getSelectionRect(anchor, focus) : null;

  const isSelected = useCallback(
    (row: number, col: number) => {
      if (!sel) return false;
      return row >= sel.minRow && row <= sel.maxRow && col >= sel.minCol && col <= sel.maxCol;
    },
    [sel],
  );

  const selectionBorders = useCallback(
    (row: number, col: number) => {
      if (!sel) return '';
      if (!isSelected(row, col)) return '';
      const classes: string[] = [];
      if (row === sel.minRow) classes.push('ss-sel-top');
      if (row === sel.maxRow) classes.push('ss-sel-bottom');
      if (col === sel.minCol) classes.push('ss-sel-left');
      if (col === sel.maxCol) classes.push('ss-sel-right');
      return classes.join(' ');
    },
    [sel, isSelected],
  );

  const handleCellClick = useCallback(
    (row: number, col: number, e: React.MouseEvent) => {
      window.getSelection()?.removeAllRanges();
      if (e.shiftKey && anchor) {
        setFocus({ row, col });
      } else {
        setAnchor({ row, col });
        setFocus({ row, col });
      }
      containerRef.current?.focus();
    },
    [anchor],
  );

  const handleRowHeaderClick = useCallback(
    (row: number, e: React.MouseEvent) => {
      window.getSelection()?.removeAllRanges();
      if (e.shiftKey && anchor) {
        setFocus({ row, col: lastCol });
        setAnchor(prev => (prev ? { row: prev.row, col: 0 } : { row, col: 0 }));
      } else {
        setAnchor({ row, col: 0 });
        setFocus({ row, col: lastCol });
      }
      containerRef.current?.focus();
    },
    [anchor, lastCol],
  );

  const handleColHeaderClick = useCallback(
    (col: number, e: React.MouseEvent) => {
      window.getSelection()?.removeAllRanges();
      if (e.shiftKey && anchor) {
        setFocus({ row: lastRow, col });
        setAnchor(prev => (prev ? { row: 0, col: prev.col } : { row: 0, col }));
      } else {
        setAnchor({ row: 0, col });
        setFocus({ row: lastRow, col });
      }
      containerRef.current?.focus();
    },
    [anchor, lastRow],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && sel) {
        e.preventDefault();
        const lines: string[] = [];
        for (let r = sel.minRow; r <= sel.maxRow; r++) {
          const cells: string[] = [];
          for (let c = sel.minCol; c <= sel.maxCol; c++) {
            cells.push(formatCell(rows[r]?.[c] ?? null));
          }
          lines.push(cells.join('\t'));
        }
        navigator.clipboard.writeText(lines.join('\n'));
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [sel, rows]);

  if (rows.length === 0) {
    return <div className="ss-empty">No data available.</div>;
  }

  return (
    <div className="ss-container" tabIndex={0} ref={containerRef}>
      <div className="ss-scroll" style={{ maxHeight }}>
        <table className="ss-table">
          <thead>
            <tr>
              {showRowNumbers && <th className="ss-corner" />}
              {headers.map((h, ci) => (
                <th
                  key={ci}
                  className={`ss-col-header${sel && ci >= sel.minCol && ci <= sel.maxCol && sel.minRow === 0 && sel.maxRow === lastRow ? ' ss-col-selected' : ''}`}
                  onClick={e => handleColHeaderClick(ci, e)}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={rowClasses?.[ri] || ''}>
                {showRowNumbers && (
                  <td
                    className={`ss-row-header${sel && ri >= sel.minRow && ri <= sel.maxRow && sel.minCol === 0 && sel.maxCol === lastCol ? ' ss-row-selected' : ''}`}
                    onClick={e => handleRowHeaderClick(ri, e)}
                  >
                    {ri + 1}
                  </td>
                )}
                {headers.map((_h, ci) => {
                  const selected = isSelected(ri, ci);
                  const borders = selectionBorders(ri, ci);
                  const cellCls = cellClasses?.[ri]?.[ci];
                  return (
                    <td
                      key={ci}
                      className={`ss-cell${selected ? ' ss-selected' : ''}${borders ? ' ' + borders : ''}${cellCls ? ' ' + cellCls : ''}`}
                      title={row[ci] != null ? String(row[ci]) : ''}
                      onClick={e => handleCellClick(ri, ci, e)}
                    >
                      {formatCell(row[ci] ?? null)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
